import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTenant } from "../hooks/useTenant";
import { getAllowedMenuIds } from "../config/tenants";
import { API_BASE_URL, AGENT_NAME } from "../config";
import useDetailViewQuota from "../hooks/useDetailViewQuota";
import {
  MessageSquare,
  LineChart,
  Search,
  History,
  LogOut,
  Settings,
  Zap,
  Bookmark,
  Briefcase,
  Bell,
  BookOpen,
  TrendingUp,
  Trophy,
  BarChart3,
  Heart,
  Map,
  Eye,
  X,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown,
  Bot,
  Plus,
  Trash2,
} from "lucide-react";
import { listChats, createChat, deleteChat, refreshChats, getCreds, subscribe as subscribeHermesChats } from "../lib/hermesChats";

const menuItems = [
  { id: "chat",        path: "/chat",        label: "Chat",        icon: MessageSquare },
  { id: "screener",    path: "/screener",    label: "Screener",    icon: Search },
  { id: "history",     path: "/history",     label: "History",     icon: History },
  { id: "portfolio",   path: "/portfolio",   label: "Portfolio",   icon: Briefcase },
  { id: "alerts",      path: "/alerts",      label: "Alerts",      icon: Bell },
  { id: "research",    path: "/research-area", label: "Research",  icon: BookOpen },
  { id: "predictions", path: "/predictions", label: "Predictions", icon: TrendingUp },
  { id: "leaderboard", path: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { id: "symbols",     path: "/symbols",     label: "Symbols",     icon: BarChart3 },
  { id: "watchlist",   path: "/watchlist",   label: "Watchlist",   icon: Bookmark },
  { id: "following",   path: "/following",   label: "Following",   icon: Heart },
  { id: "sector-map",  path: "/sector-map",  label: "Sector Map",  icon: Map },
  { id: "chart",       path: "/chart",       label: "Chart",       icon: LineChart },
];

// Collapse certain menu items under an expandable parent (e.g. "Prediction").
// `parentId` reuses an existing menuItems entry as the group header (so the
// parent label/icon/path come from there); `childIds` are nested under it.
const NAV_GROUPS = [
  { id: "predictions", label: "Prediction", childIds: ["leaderboard", "symbols", "following", "sector-map"] },
];

// Desired top-level order. Ids not listed keep their original order, appended after.
const TOP_LEVEL_ORDER = ["chart", "predictions", "watchlist"];

function HermesChatsNav({ collapsed, userId, onNavigate }) {
  const navigate = useNavigate();
  const [chats, setChats] = useState(() => listChats(userId));
  useEffect(() => {
    setChats(listChats(userId));
    return subscribeHermesChats(() => setChats(listChats(userId)));
  }, [userId]);

  // Self-populate from the server using cached creds — independent of the chat
  // page's broker call. This makes the sidebar show history even when the chat is
  // mid-(re)connect or the broker cold-wake is slow, and on a returning device the
  // moment creds are present. On a brand-new device with no creds yet, it stays
  // empty until the first agent visit caches creds (then this fills it). Refreshes
  // on mount + when the tab regains focus.
  useEffect(() => {
    const pull = () => {
      const creds = getCreds();
      if (creds) refreshChats(userId, creds.baseUrl, creds.token).catch(() => { /* sandbox asleep/transient */ });
    };
    pull();
    const onFocus = () => { if (document.visibilityState === 'visible') pull(); };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [userId]);

  const handleNew = () => {
    const c = createChat(userId);
    onNavigate?.();
    navigate(`/agentbro/${c.id}`);
  };

  // Collapsed sidebar — just show the bot icon as a shortcut to /agentbro.
  if (collapsed) {
    return (
      <NavLink
        to="/agentbro"
        title={AGENT_NAME}
        onClick={onNavigate}
        className={({ isActive }) =>
          `group flex items-center justify-center py-2.5 text-sm font-medium transition-all
          ${isActive
            ? 'text-[#4F8EF7] border-l-2 border-[#4F8EF7] bg-white/5'
            : 'text-[#C2C6D5] opacity-70 hover:bg-white/10 hover:text-white hover:opacity-100'}`
        }
      >
        <Bot size={18} />
      </NavLink>
    );
  }

  return (
    <div className="mt-4">
      <div className="px-8 mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#C2C6D5] opacity-60">
          <Bot size={12} />
          <span>{AGENT_NAME}</span>
        </div>
        <button
          onClick={handleNew}
          title="New chat"
          className="text-[#C2C6D5] opacity-60 hover:text-white hover:opacity-100 transition p-0.5 rounded"
        >
          <Plus size={14} />
        </button>
      </div>
      {chats.length === 0 ? (
        <div className="px-8 py-1 text-[11px] text-[#C2C6D5] opacity-40 italic">No chats yet</div>
      ) : (
        <div className="space-y-0.5">
          {chats.slice(0, 10).map((c) => (
            <NavLink
              key={c.id}
              to={`/agentbro/${c.id}`}
              onClick={onNavigate}
              title={c.title}
              className={({ isActive }) =>
                `group flex items-center px-8 py-1.5 text-[13px] transition-all
                ${isActive
                  ? 'text-[#4F8EF7] bg-white/5'
                  : 'text-[#C2C6D5] opacity-70 hover:bg-white/10 hover:text-white hover:opacity-100'}`
              }
            >
              <span className="truncate flex-1">{c.title}</span>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (confirm(`Delete this chat? (${AGENT_NAME} will keep the server-side history.)`)) {
                    deleteChat(userId, c.id);
                  }
                }}
                title="Delete from list"
                className="opacity-0 group-hover:opacity-60 hover:!opacity-100 ml-2 transition"
              >
                <Trash2 size={12} />
              </button>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// A single leaf nav link. `indent` nudges submenu items to the right.
function NavItem({ item, collapsed, onNavigate, indent = false }) {
  const { path, label, icon: Icon } = item;
  return (
    <NavLink
      to={path}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group flex items-center py-2.5 text-sm font-medium duration-300 transition-all
        ${collapsed ? 'justify-center px-0' : `${indent ? 'pl-14 pr-8' : 'px-8'} hover:translate-x-1`}
        ${isActive
          ? 'text-[#4F8EF7] border-l-2 border-[#4F8EF7] bg-white/5'
          : 'text-[#C2C6D5] opacity-70 hover:bg-white/10 hover:text-white hover:opacity-100'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={indent ? 16 : 18} className={collapsed ? '' : 'mr-3 flex-shrink-0'} strokeWidth={isActive ? 2.5 : 2} />
          {!collapsed && label}
        </>
      )}
    </NavLink>
  );
}

// An expandable parent ("Prediction") whose label/icon/path come from `parent`
// and which nests `children` underneath. Collapsed sidebar → just the parent link.
function NavGroupItem({ parent, groupLabel, children, collapsed, onNavigate }) {
  const location = useLocation();
  const childPaths = children.map((c) => c.path);
  const isChildActive = childPaths.some((p) => location.pathname.startsWith(p));
  const isParentActive = location.pathname.startsWith(parent.path);
  const [open, setOpen] = useState(isChildActive || isParentActive);
  // Keep it open whenever a route inside the group is active.
  useEffect(() => {
    if (isChildActive || isParentActive) setOpen(true);
  }, [isChildActive, isParentActive]);

  const Icon = parent.icon;

  // Collapsed sidebar: render the parent as a plain icon link (no expansion UI).
  if (collapsed) {
    return <NavItem item={{ ...parent, label: groupLabel }} collapsed={collapsed} onNavigate={onNavigate} />;
  }

  return (
    <div>
      <div
        className={`group flex items-center pl-8 pr-4 py-2.5 text-sm font-medium transition-all
          ${isParentActive
            ? 'text-[#4F8EF7] border-l-2 border-[#4F8EF7] bg-white/5'
            : 'text-[#C2C6D5] opacity-70 hover:bg-white/10 hover:text-white hover:opacity-100'}`}
      >
        <NavLink
          to={parent.path}
          onClick={onNavigate}
          className="flex items-center flex-1 min-w-0 hover:translate-x-1 transition-transform"
        >
          <Icon size={18} className="mr-3 flex-shrink-0" strokeWidth={isParentActive ? 2.5 : 2} />
          <span className="truncate">{groupLabel}</span>
        </NavLink>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
          aria-label={open ? `Collapse ${groupLabel}` : `Expand ${groupLabel}`}
          className="p-0.5 rounded opacity-60 hover:opacity-100 hover:text-white transition"
        >
          <ChevronDown size={16} className={`transition-transform duration-200 ${open ? '' : '-rotate-90'}`} />
        </button>
      </div>
      {open && (
        <div className="space-y-0.5">
          {children.map((child) => (
            <NavItem key={child.id} item={child} collapsed={collapsed} onNavigate={onNavigate} indent />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ isOpen, onClose }) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const { quota } = useDetailViewQuota();
  const { subdomain } = useTenant();
  const allowedIds = getAllowedMenuIds(subdomain);
  const visibleMenuItems = menuItems.filter((item) => allowedIds.includes(item.id));

  // Build the top-level nav: collapse NAV_GROUPS children under their parent,
  // then order by TOP_LEVEL_ORDER. Items/children not visible for this tenant
  // are dropped; a group with no visible children renders as a normal leaf.
  const byId = Object.fromEntries(menuItems.map((m) => [m.id, m]));
  const visibleIds = new Set(visibleMenuItems.map((m) => m.id));
  const activeGroups = NAV_GROUPS
    .filter((g) => visibleIds.has(g.id))
    .map((g) => ({
      ...g,
      childItems: g.childIds.map((cid) => byId[cid]).filter((c) => c && visibleIds.has(c.id)),
    }))
    .filter((g) => g.childItems.length > 0);
  const groupedChildIds = new Set(activeGroups.flatMap((g) => g.childItems.map((c) => c.id)));
  const topEntries = visibleMenuItems
    .filter((m) => !groupedChildIds.has(m.id))
    .map((m) => {
      const grp = activeGroups.find((g) => g.id === m.id);
      return grp
        ? { type: 'group', id: m.id, parent: m, label: grp.label, children: grp.childItems }
        : { type: 'leaf', id: m.id, item: m };
    })
    .sort((a, b) => {
      const ia = TOP_LEVEL_ORDER.indexOf(a.id);
      const ib = TOP_LEVEL_ORDER.indexOf(b.id);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', String(!prev));
      return !prev;
    });
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    axios
      .post(`${API_BASE_URL}/api/payments/token/verify/`, { token: localStorage.getItem('token') })
      .then((res) => { if (res.data?.user?.is_premium) setIsPremium(true); })
      .catch(() => {});
  }, [isAuthenticated]);

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-50 flex flex-col bg-[#2E3037] shadow-2xl shadow-[#2E3037]/20 overflow-y-auto overflow-x-hidden
        transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${collapsed ? 'w-16' : 'w-64'}
        md:relative md:translate-x-0 md:z-auto md:flex-shrink-0 md:h-screen
      `}
    >
      {/* Brand */}
      <div className={`py-5 border-b border-white/5 flex items-center ${collapsed ? 'justify-center px-0' : 'px-8 justify-between'}`}>
        {!collapsed && (
          <div>
            <h1 className="font-bold text-white text-xl tracking-tight">TraderBro</h1>
            <p className="text-[#C2C6D5] opacity-60 text-[10px] uppercase tracking-widest mt-1">
              Finfluencer Accountability, Quantified.
            </p>
          </div>
        )}
        <div className={`flex items-center ${collapsed ? 'flex-col gap-2' : 'gap-1'}`}>
          {/* Mobile close */}
          <button
            onClick={onClose}
            className="md:hidden text-[#C2C6D5] hover:text-white transition-colors p-1"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
          {/* Collapse toggle — desktop only */}
          <button
            onClick={toggleCollapsed}
            className="hidden md:flex text-[#C2C6D5] opacity-50 hover:opacity-100 hover:text-white transition-all p-1 rounded"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
        {/* AgentBro at the top */}
        <HermesChatsNav collapsed={collapsed} userId={user?.id} onNavigate={onClose} />

        {/* Divider between AgentBro and the rest */}
        {!collapsed && topEntries.length > 0 && (
          <div className="my-2 mx-8 border-t border-white/5" />
        )}

        {topEntries.map((entry) =>
          entry.type === 'group' ? (
            <NavGroupItem
              key={entry.id}
              parent={entry.parent}
              groupLabel={entry.label}
              children={entry.children}
              collapsed={collapsed}
              onNavigate={onClose}
            />
          ) : (
            <NavItem key={entry.id} item={entry.item} collapsed={collapsed} onNavigate={onClose} />
          )
        )}
      </nav>

      {/* Bottom section */}
      <div className={`pt-4 pb-6 border-t border-white/5 space-y-2 ${collapsed ? 'px-0' : 'px-6'}`}>
        {/* Detail view quota pill — hidden when collapsed */}
        {!collapsed && quota && (() => {
          const used = quota.used_today ?? quota.used ?? 0;
          const limit = quota.daily_limit ?? quota.limit ?? 0;
          const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
          const barColor = pct >= 100 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-[#4F8EF7]';
          const textColor = pct >= 100 ? 'text-red-400' : pct >= 70 ? 'text-amber-400' : 'text-[#C2C6D5]';
          return (
            <div className="px-1 py-2 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Eye size={12} className={textColor} />
                  <span className="text-[11px] text-[#C2C6D5] opacity-70">Detail Views</span>
                </div>
                <span className={`text-[11px] font-bold font-mono ${textColor}`}>
                  {used}/{limit}
                </span>
              </div>
              <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-[10px] text-[#C2C6D5] opacity-50">
                {limit - used > 0
                  ? `${limit - used} of ${limit} remaining today`
                  : 'Limit reached — resets tomorrow'}
              </p>
            </div>
          );
        })()}

        {!isPremium && (
          <button
            onClick={() => navigate('/upgrade')}
            title={collapsed ? 'Upgrade' : undefined}
            className={`w-full flex items-center rounded-xl bg-gradient-to-br from-[#4F8EF7] to-[#2c72d9] text-white text-sm font-bold shadow-lg shadow-[#4F8EF7]/20 hover:brightness-110 active:scale-95 transition-all
              ${collapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'}`}
          >
            <Zap size={16} className="text-yellow-300 fill-yellow-300 flex-shrink-0" />
            {!collapsed && 'Upgrade'}
          </button>
        )}

        <button
          onClick={() => navigate('/settings')}
          title={collapsed ? 'Settings' : undefined}
          className={`w-full flex items-center rounded-lg text-[#C2C6D5] opacity-70 hover:bg-white/10 hover:opacity-100 hover:text-white text-sm transition-all
            ${collapsed ? 'justify-center p-2.5' : 'gap-3 px-4 py-2.5'}`}
        >
          <Settings size={16} className="flex-shrink-0" />
          {!collapsed && 'Settings'}
        </button>

        {/* User row */}
        <div className={`flex items-center py-2 mt-1 ${collapsed ? 'justify-center flex-col gap-2 px-0' : 'gap-3 px-4'}`}>
          <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 text-white font-medium text-sm">
            {user?.first_name?.[0] || "U"}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {user?.first_name || "User"}
                </div>
                <div className="text-xs text-[#C2C6D5] opacity-60 truncate">
                  {user?.email}
                </div>
              </div>
              <button
                onClick={logout}
                className="text-[#C2C6D5] opacity-60 hover:text-red-400 hover:opacity-100 transition-colors"
                title="Log out"
              >
                <LogOut size={16} />
              </button>
            </>
          )}
          {collapsed && (
            <button
              onClick={logout}
              className="text-[#C2C6D5] opacity-60 hover:text-red-400 hover:opacity-100 transition-colors"
              title="Log out"
            >
              <LogOut size={14} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
